/** Staff — Training Analytics: monthly turnover, marketing costs → profit, attendance & no-shows. */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, TrendingUp, PoundSterling, Wallet, Loader2, Calendar as CalendarIcon, UserX } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { BRAND } from '@/pages/trainingBrand';
import { StatTile, gbp } from '@/components/training/analytics/shared';
import { MonthlyTable, TurnoverChart } from '@/components/training/analytics/TurnoverSection';
import { AttendanceSection } from '@/components/training/analytics/AttendanceSection';
import { CoursesTable, OutstandingSection } from '@/components/training/analytics/CoursesSection';
import { SourceSection } from '@/components/training/analytics/SourceSection';
import { LeadSourceSection } from '@/components/training/analytics/LeadSourceSection';
import { PaymentsLog } from '@/components/training/analytics/PaymentsLog';

const TABS = [{ key: 'overview', label: 'Overview' }, { key: 'payments', label: 'Payments log' }];

const GREEN = BRAND.green;
const STAFF = ['admin', 'manager', 'team'];
const iso = (d) => d.toISOString().slice(0, 10);
const startOfMonthsAgo = (n) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - n); return iso(d); };
const endOfThisMonth = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };

export default function TrainingAnalytics() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [start, setStart] = useState(startOfMonthsAgo(5));
  const [end, setEnd] = useState(endOfThisMonth());
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');

  useEffect(() => { if (user && !STAFF.includes(user.role)) navigate('/', { replace: true }); }, [user, navigate]);

  const run = useCallback(async (quiet = false) => {
    if (!start || !end) { toast.error('Pick both dates first.'); return; }
    if (!quiet) setBusy(true);
    try {
      const qs = new URLSearchParams({ start_date: start, end_date: end });
      const res = await fetch(`${API_URL}/api/staff/training-analytics?${qs}`, { credentials: 'include' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Report failed');
      setData(await res.json());
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }, [start, end]);

  useEffect(() => { if (user && STAFF.includes(user.role)) run(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const presets = [
    { label: 'This month', s: startOfMonthsAgo(0), e: endOfThisMonth() },
    { label: 'Last 3 months', s: startOfMonthsAgo(2), e: endOfThisMonth() },
    { label: 'Last 6 months', s: startOfMonthsAgo(5), e: endOfThisMonth() },
    { label: 'Last 12 months', s: startOfMonthsAgo(11), e: endOfThisMonth() },
    { label: 'This year', s: `${new Date().getFullYear()}-01-01`, e: `${new Date().getFullYear()}-12-31` },
  ];
  const t = data?.totals;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${GREEN.tint} 0%, #FFFFFF 300px)` }}>
      <div className="border-b sticky top-0 z-20 shadow-sm" style={{ background: GREEN.primary, color: GREEN.ink }}>
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Link to="/staff/training-courses" className="text-white/80 hover:text-white" data-testid="training-analytics-back"><ArrowLeft className="w-5 h-5" /></Link>
          <Link to="/" className="text-white/80 hover:text-white hidden sm:inline-flex"><Home className="w-5 h-5" /></Link>
          <div className="w-8 h-8 rounded-lg bg-white/15 text-white flex items-center justify-center"><TrendingUp className="w-4 h-4" /></div>
          <h1 className="text-base sm:text-lg font-bold text-white truncate flex-1">Training Analytics</h1>
          <Link to="/staff/training-report" className="hidden md:inline text-xs text-white/80 hover:text-white underline shrink-0" data-testid="training-report-link">Revenue report</Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <Card className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[9rem]"><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white" data-testid="analytics-start" /></div>
            <div className="flex-1 min-w-[9rem]"><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white" data-testid="analytics-end" /></div>
            <Button onClick={() => run()} disabled={busy} className="text-white font-bold" style={{ background: GREEN.primary }} data-testid="analytics-run">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarIcon className="w-4 h-4 mr-1.5" />}{busy ? 'Loading…' : 'Run'}
            </Button>
          </div>
          <div className="pill-row mt-3">
            {presets.map((p) => (
              <button key={p.label} type="button" onClick={() => { setStart(p.s); setEnd(p.e); }} className="shrink-0 text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid={`analytics-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}>{p.label}</button>
            ))}
          </div>
        </Card>

        <div className="flex gap-2" data-testid="analytics-tabs">
          {TABS.map((x) => <button key={x.key} type="button" onClick={() => setTab(x.key)} data-testid={`analytics-tab-${x.key}`} className={`px-3 py-1.5 rounded-lg text-sm font-bold border-2 ${tab === x.key ? 'text-white border-transparent' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`} style={tab === x.key ? { background: GREEN.primary } : undefined}>{x.label}</button>)}
        </div>

        {tab === 'payments' && <PaymentsLog start={start} end={end} />}

        {tab === 'overview' && data && (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <StatTile icon={PoundSterling} label="Turnover (billed)" value={gbp(t.billed)} sub={`${gbp(t.billed_ex_vat)} ex VAT · ${t.seats} seats`} tone="good" testid="stat-billed" />
              <StatTile icon={Wallet} label="Cash received" value={gbp(t.cash)} sub={t.refunds ? `Net of ${gbp(t.refunds)} refunds` : 'Payments dated in range'} tone="info" testid="stat-cash" />
              <StatTile icon={UserX} label="No-shows" value={t.no_shows} sub={t.attendance_rate === null ? 'No past courses yet' : `${t.attendance_rate}% attendance`} tone={t.no_shows ? 'bad' : 'default'} testid="stat-no-shows" />
            </div>
            <TurnoverChart months={data.months} />
            <MonthlyTable data={data} reload={() => run(true)} />
            <AttendanceSection data={data} />
            <SourceSection rows={data.by_source || []} />
            <LeadSourceSection data={data.lead_sources} start={start} end={end} />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3"><CoursesTable courses={data.courses} /></div>
              <div className="lg:col-span-2"><OutstandingSection outstanding={data.outstanding} /></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
